/**
 * Structured copyright-page content shared by HTML and native PDF (#295).
 */
import type { ZwCopyrightPage, ZwMetadata } from "../lib/zwConfig";
import { just, nothing, type Maybe } from "../lib/maybe";

export type CopyrightParagraphKind =
  "notice" | "body" | "credit" | "edition" | "imprint" | "url";

export type CopyrightParagraph = {
  readonly text: string;
  readonly kind: CopyrightParagraphKind;
};

function yearFromPubDate(pubDate: string): string {
  const trimmed = pubDate.trim();
  const match = trimmed.match(/\b(19|20)\d{2}\b/);
  return match?.[0] ?? "";
}

function copyrightNotice(meta: ZwMetadata): string {
  const rights = meta.rights.trim();
  if (rights !== "") return rights;
  const fromPub = yearFromPubDate(meta.pubDate);
  const year = fromPub !== "" ? fromPub : String(new Date().getFullYear());
  const author = meta.author.trim();
  return author !== "" ? `© ${year} ${author}` : `© ${year}`;
}

function editionLine(page: ZwCopyrightPage, meta: ZwMetadata): string {
  const editionOverride = page.editionLine.trim();
  if (editionOverride !== "") return editionOverride;
  const pubTrimmed = meta.pubDate.trim();
  const date = pubTrimmed !== "" ? pubTrimmed : yearFromPubDate(meta.pubDate);
  if (date === "") return "";
  return `First paperback edition ${date}`;
}

function publishedByLine(page: ZwCopyrightPage, meta: ZwMetadata): string {
  const override = page.publishedBy.trim();
  if (override !== "") return `Published by ${override}`;
  const publisher = meta.publisher.trim();
  if (publisher !== "") return `Published by ${publisher}`;
  const author = meta.author.trim();
  if (author !== "") return `Published by ${author}`;
  return "";
}

function rightsParagraph(author: string): string {
  const contact = author !== "" ? author : "the author";
  return (
    `All rights reserved. The scanning, uploading, and distribution of this book ` +
    `without permission is a theft of the author's intellectual property. If you ` +
    `would like permission to use material from the book (other than for review ` +
    `purposes), please contact ${contact}. Thank you for your support of the ` +
    `author's rights.`
  );
}

const AI_RESTRICTION =
  "No part of this book may be used or reproduced in any manner for the purpose " +
  "of training artificial intelligence technologies or systems.";

function paragraphsFromAdditional(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((block) => block.trim())
    .filter((block) => block !== "");
}

/** Structured copyright paragraphs, or Nothing when disabled / empty. */
export function buildCopyrightPageContent(
  page: ZwCopyrightPage,
  meta: ZwMetadata,
): Maybe<readonly CopyrightParagraph[]> {
  if (page.enabled !== true) return nothing;

  const blocks: CopyrightParagraph[] = [];
  const notice = copyrightNotice(meta);
  if (notice !== "") {
    blocks.push({ text: notice, kind: "notice" });
  }

  if (page.includeRightsParagraph === true) {
    blocks.push({
      text: rightsParagraph(meta.author.trim()),
      kind: "body",
    });
  }

  if (page.includeAiRestriction === true) {
    blocks.push({ text: AI_RESTRICTION, kind: "body" });
  }

  for (const credit of page.credits) {
    const role = credit.role.trim();
    const name = credit.name.trim();
    if (role === "" && name === "") continue;
    const text =
      role !== "" && name !== ""
        ? `${role} © ${name}`
        : role !== ""
          ? role
          : `© ${name}`;
    blocks.push({ text, kind: "credit" });
  }

  const edition = editionLine(page, meta);
  if (edition !== "") {
    blocks.push({ text: edition, kind: "edition" });
  }

  const published = publishedByLine(page, meta);
  if (published !== "") {
    blocks.push({ text: published, kind: "imprint" });
  }

  const url = page.authorUrl.trim();
  if (url !== "") {
    blocks.push({ text: url, kind: "url" });
  }

  const isbn = meta.isbn.trim();
  if (isbn !== "") {
    blocks.push({ text: `ISBN ${isbn}`, kind: "imprint" });
  }

  const lccn = meta.lccn.trim();
  if (lccn !== "") {
    blocks.push({
      text: `Library of Congress Control Number: ${lccn}`,
      kind: "imprint",
    });
  }

  for (const para of paragraphsFromAdditional(page.additionalText)) {
    blocks.push({ text: para.replace(/\n/g, " "), kind: "body" });
  }

  if (blocks.length === 0) return nothing;
  return just(blocks);
}
